import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ShieldCheck } from 'lucide-react';
import { Image } from '@/components/ui/image';
import { ROSTER } from '@/data/roster';
import Flag from '@/components/Flag';

export default function Hero() {
  const featured = ROSTER.slice(0, 4);

  return (
    <section id="top" className="relative bg-[#0B0B0B] pt-32 lg:pt-40 pb-20 lg:pb-28 overflow-hidden">
      <Image
        src="https://media.base44.com/images/public/6a68984e1435c2ee512e6e95/d441474b8_generated_image.png"
        alt=""
        fittingType="fill"
        quality={55}
        className="absolute inset-0 w-full h-full object-cover opacity-40"
      />
      <div className="absolute inset-0 bg-gradient-to-r from-[#0B0B0B] via-[#0B0B0B]/85 to-[#0B0B0B]/55" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0B0B0B] via-transparent to-[#0B0B0B]/70" />
      <div className="absolute inset-0 fine-grid opacity-25" />
      <div className="absolute top-0 right-0 w-2/3 h-full bg-[radial-gradient(circle_at_top_right,rgba(37,99,235,0.18),transparent_58%)]" />

      <div className="relative max-w-[1320px] mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-14 lg:gap-16 items-center">
        <div>
          <div className="inline-flex items-center gap-2.5 mb-7 px-4 py-2 rounded-lg border border-[#3B82F6]/30 bg-[#2563EB]/10">
            <ShieldCheck className="w-3.5 h-3.5 text-[#3B82F6]" />
            <span className="text-[11px] font-semibold uppercase tracking-[0.16em] text-white/80">
              London & Cape Town · Digital Agency
            </span>
          </div>

          {/* Rendered without animation: this is the LCP element, so any
              JS-driven fade pushes the largest paint later. */}
          <h1
            className="font-display font-bold text-white tracking-tight leading-[1.04]"
            style={{ fontSize: 'clamp(2.4rem, 5.2vw, 4.6rem)' }}
          >
            The Next Generation of Global Influence and Creative Production.
          </h1>

          <p className="mt-7 text-white/60 text-base lg:text-lg leading-relaxed font-light max-w-xl">
            Ahana Digital bridges the gap between premium global talent, data-driven social
            virality, and cutting-edge digital asset creation. We engineer cultural moments.
          </p>

          <div className="mt-10 flex flex-col sm:flex-row gap-4">
            <Link
              to="/#roster"
              className="group inline-flex items-center justify-center gap-2 px-8 h-14 rounded-lg text-sm font-semibold bg-[#2563EB] text-white hover:bg-[#1D4ED8] transition-colors duration-300 blue-glow"
            >
              View Our Roster
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link
              to="/#services"
              className="inline-flex items-center justify-center px-8 h-14 rounded-lg text-sm font-semibold border border-white/20 text-white hover:border-white/50 transition-colors duration-300"
            >
              Corporate Services
            </Link>
          </div>

          <div className="mt-14 grid grid-cols-3 gap-6 max-w-lg">
            {[
              { k: '10', v: 'Managed Creators' },
              { k: '9', v: 'Global Markets' },
              { k: '48M+', v: 'Combined Reach' },
            ].map((s) => (
              <div key={s.v}>
                <div className="font-display font-bold text-2xl lg:text-3xl text-white">{s.k}</div>
                <div className="text-[11px] uppercase tracking-[0.14em] text-white/45 mt-1.5 leading-tight">{s.v}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="relative rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-sm p-5 lg:p-7">
          <div className="flex items-center justify-between mb-5">
            <h2 className="font-display font-semibold text-white text-lg">Elite Talent Roster</h2>
            <span className="text-[10px] uppercase tracking-[0.18em] text-[#3B82F6]">Featured</span>
          </div>

          <div className="grid grid-cols-2 gap-4">
            {featured.map((m) => (
              <Link
                key={m.slug}
                to={`/talent/${m.slug}`}
                className="group rounded-xl overflow-hidden border border-white/10 bg-[#121212] hover:border-[#3B82F6]/60 transition-colors duration-500"
              >
                <div className="aspect-[3/4] overflow-hidden">
                  <Image
                    src={m.img}
                    alt={m.name}
                    fittingType="fill"
                    quality={70}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
                <div className="p-3.5">
                  <div className="flex items-center gap-2">
                    <Flag code={m.code} className="w-[18px] h-3" />
                    <h3 className="font-display font-semibold text-sm text-white truncate">{m.name}</h3>
                  </div>
                  <p className="text-[11px] text-white/45 mt-1 truncate">{m.specialization}</p>
                </div>
              </Link>
            ))}
          </div>

          <Link
            to="/#roster"
            className="mt-5 flex items-center justify-center gap-2 h-12 rounded-lg border border-white/10 text-xs font-semibold uppercase tracking-[0.15em] text-white/70 hover:text-[#3B82F6] hover:border-[#3B82F6]/40 transition-colors"
          >
            See All 10 Creators
            <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      </div>
    </section>
  );
}